import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import Image from 'components/generic/Image';
import type { ImageProps } from 'components/generic/Image/shared/types';
import { Typography } from 'components/generic/typography';
import PageAction, { type PageActionProps } from 'components/shared/Routing/PageAction';
import type { ToTransientProps } from 'types/styled';

type BackgroundColor = 'white' | 'grey';

export type ShortcutProps = PageActionProps & {
    imageProps: ImageProps;
    backgroundColor?: BackgroundColor;
    label: string;
};

type StyledCircleProps = Required<Pick<ToTransientProps<ShortcutProps>, '$backgroundColor'>>;

function getBackgroundColor(backgroundColor: BackgroundColor) {
    switch (backgroundColor) {
        case 'grey':
            return css`
                ${({ theme }) => theme.ColorBackgroundSecondary}
            `;
        case 'white':
        default:
            return css`
                ${({ theme }) => theme.ColorBackgroundPrimary}
            `;
    }
}

const StyledPageAction = styled(PageAction)`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${({ theme }) => theme.Spacing8};
`;

const Circle = styled.div<StyledCircleProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 96px;
    height: 96px;
    border-radius: 50%;
    overflow: hidden;

    background-color: ${({ $backgroundColor }) => getBackgroundColor($backgroundColor)};

`;

const StyledImage = styled(Image)`
    width: calc(100% - ${({ theme, objectFit }) => (objectFit === 'contain' ? theme.Spacing16 : '0px')});
    height: calc(100% - ${({ theme, objectFit }) => (objectFit === 'contain' ? theme.Spacing16 : '0px')});
`;

const Shortcut = forwardRef<HTMLAnchorElement | HTMLButtonElement, ShortcutProps>((props, ref) => {
    const { action, label, backgroundColor = 'white', imageProps, ...restProps } = props;
    const { src, objectFit = 'cover', alt, ...imageRestProps } = imageProps;

    return (
        <StyledPageAction action={action} {...restProps} ref={ref}>
            <Circle $backgroundColor={backgroundColor}>
                <StyledImage src={src} alt={alt} objectFit={objectFit} {...imageRestProps} />
            </Circle>
            <Typography.Body size="M">{label}</Typography.Body>
        </StyledPageAction>
    );
});

export default Shortcut;
